import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const SB_URL = process.env.REACT_APP_SUPABASE_URL;
const SB_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const projectRef = SB_URL ? new URL(SB_URL).hostname.split('.')[0] : '';

function getAccessToken() {
  try {
    const raw = localStorage.getItem(`sb-${projectRef}-auth-token`);
    if (raw) return JSON.parse(raw)?.access_token || null;
  } catch { /* ignore */ }
  return null;
}

async function sbFetch(path, options = {}) {
  const token = getAccessToken();
  const headers = {
    'apikey': SB_KEY,
    'Authorization': `Bearer ${token || SB_KEY}`,
    'Content-Type': 'application/json',
    ...options.headers,
  };
  const res = await fetch(`${SB_URL}/rest/v1/${path}`, { ...options, headers });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${body}`);
  }
  return res.json();
}

async function sbPatch(table, match, body) {
  const token = getAccessToken();
  const headers = {
    'apikey': SB_KEY,
    'Authorization': `Bearer ${token || SB_KEY}`,
    'Content-Type': 'application/json',
    'Prefer': 'return=representation',
  };
  const res = await fetch(`${SB_URL}/rest/v1/${table}?${match}`, {
    method: 'PATCH', headers, body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

const CompanyContext = createContext({
  companies: [],
  activeCompany: null,
  activeProfile: null,
  setActiveCompany: () => {},
  loading: true,
  refetch: () => {},
});

// Map Supabase company row to the companyConfig shape AppContent expects
function toCompanyConfig(company, profile) {
  if (!company) return null;
  return {
    companyId: company.id,
    companyName: company.name,
    fiscalYearEndMonth: company.fiscal_year_end_month,
    fiscalYearEndDay: company.fiscal_year_end_day,
    industry: company.industry,
    currency: company.currency,
    erp: company.erp,
    multiCurrency: company.multi_currency,
    role: profile?.role || 'preparer',
  };
}

export function CompanyProvider({ children }) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [profiles, setProfiles] = useState([]);
  // SECURITY: Store the cached ID but don't trust it until validated
  // against the server in fetchCompanies()
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const cachedCompanyId = useRef(localStorage.getItem('finkloz_active_company_id'));
  const [loading, setLoading] = useState(true);

  const fetchCompanies = useCallback(async () => {
    if (!user || !SB_URL || !SB_KEY) {
      setCompanies([]);
      setProfiles([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // First, claim any pending invites matching this user's email
      try {
        const pending = await sbFetch(
          `profiles?user_id=is.null&email=eq.${encodeURIComponent(user.email)}&select=id`
        );
        if (pending?.length > 0) {
          for (const row of pending) {
            await sbPatch('profiles', `id=eq.${row.id}`, { user_id: user.id });
          }
        }
      } catch { /* invite claim is best-effort */ }

      // Fetch user's profiles with company data
      const userProfiles = await sbFetch(
        `profiles?user_id=eq.${user.id}&select=id,company_id,role,full_name,initials,email,companies(id,name,fiscal_year_end_month,fiscal_year_end_day,industry,erp,currency,multi_currency)`
      );

      const companyList = (userProfiles || [])
        .filter(p => p.companies)
        .map(p => ({ ...p.companies, _profile: p }));

      setProfiles(userProfiles || []);
      setCompanies(companyList);

      // SECURITY: Validate cached company ID against server response
      // Only use cached ID if user actually has access to that company
      const cached = cachedCompanyId.current;
      const hasAccessToCached = cached && companyList.find(c => c.id === cached);

      setActiveCompanyId(prev => {
        if (hasAccessToCached) {
          return cached;
        }
        if (companyList.length === 1) {
          localStorage.setItem('finkloz_active_company_id', companyList[0].id);
          return companyList[0].id;
        }
        // No valid cached company -- force selection
        localStorage.removeItem('finkloz_active_company_id');
        return null;
      });
      cachedCompanyId.current = null; // Only use cache once
    } catch (err) {
      console.error('CompanyContext fetch error:', err);
      setCompanies([]);
      setProfiles([]);
    }
    setLoading(false);
  }, [user]); // only depend on user, not activeCompanyId

  useEffect(() => {
    if (user) fetchCompanies();
    else {
      setCompanies([]);
      setProfiles([]);
      setActiveCompanyId(null);
      setLoading(false);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setActiveCompany = useCallback((companyId) => {
    setActiveCompanyId(companyId);
    if (companyId) {
      localStorage.setItem('finkloz_active_company_id', companyId);
    } else {
      localStorage.removeItem('finkloz_active_company_id');
    }
  }, []);

  const activeCompanyObj = companies.find(c => c.id === activeCompanyId) || null;
  const activeProfile = activeCompanyObj?._profile || null;
  const activeCompany = toCompanyConfig(activeCompanyObj, activeProfile);

  return (
    <CompanyContext.Provider value={{
      companies,
      profiles,
      activeCompany,
      activeProfile,
      activeCompanyId,
      setActiveCompany,
      loading,
      refetch: fetchCompanies,
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

export const useCompany = () => useContext(CompanyContext);
